import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../../contexts/SupabaseContext';
import {
  ContinuingReviewQueueRow,
  listContinuingReviewQueue,
} from '../../services/continuingReviewService';
import { getContinuingReviewFlags } from '../../services/featureFlagService';
import { adminReviewRoute } from '../../utils/submissionRoutes';
import ReminderModal from './ReminderModal';
import CrMetadataModal from './CrMetadataModal';
import {
  CHIP_STATES,
  DEFAULT_QUEUE_STATES,
  DEFAULT_WINDOW_DAYS,
  formatDaysUntilExpiry,
  relativeTimeFrom,
  REMINDER_DISABLED_STATES,
  stateMeta,
  WINDOW_DAY_OPTIONS,
} from './continuingReviewQueueUtils';
import { formatDateOnly } from '../../utils/dateUtils';

const DEFAULT_SELECTED: string[] = [...DEFAULT_QUEUE_STATES];
const ALL_INCLUDE_STATES: string[] = [...DEFAULT_QUEUE_STATES, 'NOT_REQUIRED'];

const ContinuingReviewQueue: React.FC = () => {
  const navigate = useNavigate();
  const supabase = useSupabase();

  const [flagsLoading, setFlagsLoading] = useState(true);
  const [adminQueueEnabled, setAdminQueueEnabled] = useState(false);

  const [windowDays, setWindowDays] = useState<number>(DEFAULT_WINDOW_DAYS);
  const [selected, setSelected] = useState<string[]>(DEFAULT_SELECTED);

  const [rows, setRows] = useState<ContinuingReviewQueueRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [reminderRow, setReminderRow] = useState<ContinuingReviewQueueRow | null>(null);
  const [metadataRow, setMetadataRow] = useState<ContinuingReviewQueueRow | null>(null);

  const notRequiredSelected = selected.includes('NOT_REQUIRED');

  // Load the server feature flags once on mount. The admin queue RPC refuses
  // when the flag is off, so we surface an informative panel instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const flags = await getContinuingReviewFlags(supabase);
      if (!cancelled) {
        setAdminQueueEnabled(flags.adminQueue);
        setFlagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supabase]);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const includeStates = notRequiredSelected ? ALL_INCLUDE_STATES : undefined;
      const data = await listContinuingReviewQueue(supabase, windowDays, includeStates);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load continuing review queue.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [supabase, windowDays, notRequiredSelected]);

  useEffect(() => {
    if (!flagsLoading && adminQueueEnabled) {
      loadQueue();
    }
  }, [flagsLoading, adminQueueEnabled, loadQueue]);

  const toggleChip = (state: string) => {
    setSelected((prev) =>
      prev.includes(state) ? prev.filter((s) => s !== state) : [...prev, state]
    );
  };

  const visibleRows = useMemo(
    () => rows.filter((row) => selected.includes(row.queue_state)),
    [rows, selected]
  );

  const metadataRows = useMemo(
    () => visibleRows.filter((row) => row.queue_state === 'METADATA_REQUIRED'),
    [visibleRows]
  );

  const mainRows = useMemo(
    () => visibleRows.filter((row) => row.queue_state !== 'METADATA_REQUIRED'),
    [visibleRows]
  );

  const renderRow = (row: ContinuingReviewQueueRow) => {
    const meta = stateMeta(row.queue_state);
    const days = formatDaysUntilExpiry(row.days_until_expiry);
    const reminderDisabled = REMINDER_DISABLED_STATES.has(row.queue_state);

    return (
      <tr key={row.source_submission_id} className="border-t hover:bg-gray-50 align-top">
        <td className="px-4 py-3">
          <div className="font-medium text-gray-900">{row.protocol_title || 'Untitled protocol'}</div>
          <div className="text-xs text-gray-500 font-mono">{row.study_id}</div>
        </td>
        <td className="px-4 py-3 text-gray-700">{row.user_email || '—'}</td>
        <td className="px-4 py-3 text-gray-700">{row.pi_email || '—'}</td>
        <td className="px-4 py-3 text-gray-700">{formatDateOnly(row.approval_date) || '—'}</td>
        <td className="px-4 py-3 text-gray-700">{formatDateOnly(row.expiry_date) || '—'}</td>
        <td className={`px-4 py-3 ${days.overdue ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
          {days.text}
        </td>
        <td className="px-4 py-3">
          <span className={`px-2 py-1 rounded-full text-xs ${meta.badgeClass}`}>{meta.label}</span>
        </td>
        <td className="px-4 py-3 text-gray-700">
          {row.last_reminder_sent_at ? (
            <div className="flex flex-col">
              <span className="text-xs">{relativeTimeFrom(row.last_reminder_sent_at)}</span>
              {row.last_reminder_status && (
                <span className="text-xs text-gray-500">{row.last_reminder_status.toUpperCase()}</span>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400">No reminders</span>
          )}
        </td>
        <td className="px-4 py-3">
          {row.active_continuing_review_submission_id ? (
            <button
              onClick={() => navigate(adminReviewRoute(row.active_continuing_review_submission_id as string))}
              className="text-[#F55D3E] hover:text-[#F9A594] text-sm"
            >
              {row.active_continuing_review_status || 'View CR'}
            </button>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-col gap-1 items-start">
            <button
              onClick={() => navigate(adminReviewRoute(row.source_submission_id))}
              className="text-sm text-gray-700 hover:text-gray-900"
            >
              View source
            </button>
            <button
              onClick={() => setReminderRow(row)}
              disabled={reminderDisabled}
              className="text-sm text-[#F55D3E] hover:text-[#F9A594] disabled:text-gray-300 disabled:cursor-not-allowed"
            >
              Send reminder
            </button>
            {row.active_continuing_review_submission_id && (
              <button
                onClick={() => navigate(adminReviewRoute(row.active_continuing_review_submission_id as string))}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View CR
              </button>
            )}
            {row.queue_state === 'METADATA_REQUIRED' && (
              <button
                onClick={() => setMetadataRow(row)}
                className="text-sm text-[#F55D3E] hover:text-[#F9A594]"
              >
                Record CR metadata
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const tableHeader = (
    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
      <tr>
        <th className="px-4 py-3 font-medium">Protocol</th>
        <th className="px-4 py-3 font-medium">Submitter</th>
        <th className="px-4 py-3 font-medium">PI</th>
        <th className="px-4 py-3 font-medium">Approval</th>
        <th className="px-4 py-3 font-medium">Expiry</th>
        <th className="px-4 py-3 font-medium">Days</th>
        <th className="px-4 py-3 font-medium">State</th>
        <th className="px-4 py-3 font-medium">Last reminder</th>
        <th className="px-4 py-3 font-medium">Active CR</th>
        <th className="px-4 py-3 font-medium">Actions</th>
      </tr>
    </thead>
  );

  if (flagsLoading) {
    return (
      <div className="p-8 text-center">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F55D3E]"></div>
          <span className="ml-2 text-gray-600">Loading continuing review queue…</span>
        </div>
      </div>
    );
  }

  if (!adminQueueEnabled) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <h2 className="text-lg font-medium text-gray-900">Continuing review queue is not enabled</h2>
        <p className="mt-2 text-sm text-gray-500">
          The continuing review admin queue feature flag is currently turned off. Enable it to view
          studies due for continuing review.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="text-xl font-bold">Continuing reviews</h2>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600" htmlFor="cr-window-select">
            Window
          </label>
          <select
            id="cr-window-select"
            className="border rounded-lg px-3 py-2 text-sm"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
          >
            {WINDOW_DAY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} days
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {CHIP_STATES.map((state) => {
          const meta = stateMeta(state);
          const active = selected.includes(state);
          return (
            <button
              key={state}
              onClick={() => toggleChip(state)}
              aria-pressed={active}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                active
                  ? `${meta.badgeClass} border-transparent`
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {meta.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 mb-4" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F55D3E]"></div>
            <span className="ml-2 text-gray-600">Loading…</span>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-x-auto mb-8">
            {mainRows.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No studies match the current filters.</div>
            ) : (
              <table className="min-w-full text-sm">
                {tableHeader}
                <tbody>{mainRows.map(renderRow)}</tbody>
              </table>
            )}
          </div>

          {selected.includes('METADATA_REQUIRED') && (
            <div>
              <h3 className="text-lg font-semibold mb-1">Metadata required</h3>
              <p className="text-sm text-gray-500 mb-3">
                These approved studies are missing a continuing review determination and need remediation.
              </p>
              <div className="bg-white rounded-lg shadow overflow-x-auto">
                {metadataRows.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No studies require metadata remediation.
                  </div>
                ) : (
                  <table className="min-w-full text-sm">
                    {tableHeader}
                    <tbody>{metadataRows.map(renderRow)}</tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {reminderRow && (
        <ReminderModal
          row={reminderRow}
          supabase={supabase}
          onClose={() => setReminderRow(null)}
          onSent={loadQueue}
        />
      )}
      {metadataRow && (
        <CrMetadataModal
          row={metadataRow}
          supabase={supabase}
          onClose={() => setMetadataRow(null)}
          onSaved={loadQueue}
        />
      )}
    </div>
  );
};

export default ContinuingReviewQueue;
